import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Collapse, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StorageIcon from '@mui/icons-material/Storage';
import { GlassPanel } from '../../../shared/components/GlassPanel';
import { IconTile } from '../../../shared/components/IconTile';
import { SectionCollapseButton, useSectionCollapse } from '../../../shared/components/SectionCollapseButton';
import { CardGrid, type CardGridAction, DashboardCardTile } from '../../../shared/components/CardGrid';
import type {
  Document,
  DocumentDashboardCard,
  ProjectDashboardCardAssignment,
  ProjectDashboardCardTab,
} from '../../../core/types/data-models';
import { AddTabDialog, TabIconGlyph } from '../dialogs/AddTabDialog';

// ── High Level Overview (project document dashboard cards) ──────────────────
//
// Grouped into user-defined tabs (ProjectDashboardCardTab), each holding an
// explicit set of DocumentDashboardCard rows (ProjectDashboardCardAssignment)
// picked from the project's documents. Mirrors MainEntitiesSection's tab
// pattern.

/** Empty section-title lookup — DashboardCardTile's "sources" footer is
 * simply omitted here since it's cross-document, unlike DocumentDetail's
 * single-document context where every card's source sections are known. */
const NO_SECTION_TITLES = new Map<number, string>();

/** Card width bounds for the High Level Overview grid — 1 to 4 of its 4 columns. */
const CARD_MIN_SPAN = 1;
const CARD_MAX_SPAN = 4;

export function HighLevelOverviewSection({
  tabs,
  assignments,
  cards,
  docById,
  canEdit,
  activeTabId,
  onTabChange,
  onAddTab,
  onDeleteTab,
  onAddCard,
  onRemoveCard,
  onMoveCard,
  onResizeCard,
}: {
  tabs: ProjectDashboardCardTab[];
  assignments: ProjectDashboardCardAssignment[];
  cards: DocumentDashboardCard[];
  docById: Map<number, Document>;
  canEdit: boolean;
  activeTabId: number | null;
  onTabChange: (tabId: number | null) => void;
  onAddTab: (name: string, icon: string | null) => Promise<unknown>;
  onDeleteTab: (id: number) => void;
  onAddCard: (cardId: number, tabId: number | null) => void;
  onRemoveCard: (assignmentId: number) => void;
  onMoveCard: (assignmentId: number, direction: 'before' | 'after') => void;
  onResizeCard: (assignmentId: number, columnSpan: number) => void;
}) {
  const [addTabOpen, setAddTabOpen] = useState(false);
  const [expanded, toggleExpanded] = useSectionCollapse('highLevelOverview');
  const navigate = useNavigate();

  if (tabs.length === 0 && assignments.length === 0 && !canEdit) return null;

  const cardById = new Map(cards.map((c) => [c.id, c]));
  const assignmentsInActiveTab = assignments
    .filter((a) => a.tabId === activeTabId)
    .sort((a, b) => a.order - b.order);
  // CardGrid items: each assignment paired with its resolved card, dropping
  // any assignment whose card no longer exists.
  const gridItems = assignmentsInActiveTab
    .map((assignment) => ({ assignment, card: cardById.get(assignment.documentDashboardCardId) }))
    .filter((item): item is { assignment: ProjectDashboardCardAssignment; card: DocumentDashboardCard } => !!item.card);
  const cardGridActions: CardGridAction<{ assignment: ProjectDashboardCardAssignment; card: DocumentDashboardCard }>[] = [
    {
      key: 'open-source',
      icon: <OpenInNewIcon fontSize="inherit" />,
      label: 'Open source document',
      variant: 'corner',
      onClick: ({ card }) => navigate(`/documents/${card.documentId}`),
    },
    ...(canEdit
      ? [
          {
            key: 'remove',
            icon: <CloseIcon fontSize="inherit" />,
            label: 'Remove from this tab',
            color: 'error' as const,
            onClick: ({ assignment }: { assignment: ProjectDashboardCardAssignment; card: DocumentDashboardCard }) =>
              onRemoveCard(assignment.id),
          },
        ]
      : []),
  ];
  const usedCardIdsInTab = new Set(assignmentsInActiveTab.map((a) => a.documentDashboardCardId));
  // Alphabetical by document name, then card title — matches the dropdown's
  // "<document name> - <card title>" label.
  const availableCards = cards
    .filter((c) => !usedCardIdsInTab.has(c.id))
    .sort((a, b) => {
      const docNameA = docById.get(a.documentId)?.name ?? '';
      const docNameB = docById.get(b.documentId)?.name ?? '';
      const docCompare = docNameA.localeCompare(docNameB);
      return docCompare !== 0 ? docCompare : a.cardTitle.localeCompare(b.cardTitle);
    });

  const showTabStrip = tabs.length > 0 || canEdit;

  return (
    <GlassPanel sx={{ p: 2.5 }}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{ mb: expanded ? (showTabStrip ? 1.5 : 2) : 0 }}
      >
        <IconTile icon={<StorageIcon fontSize="inherit" />} color="secondary" size="sm" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            High Level Overview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Key metrics pinned from across your project&apos;s documents
          </Typography>
        </Box>
        <SectionCollapseButton expanded={expanded} onToggle={toggleExpanded} label="High Level Overview" />
      </Stack>

      <Collapse in={expanded}>
        {showTabStrip && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ mb: 1.5, rowGap: 1 }}>
            <Chip
              label="General"
              size="small"
              color={activeTabId === null ? 'primary' : 'default'}
              variant={activeTabId === null ? 'filled' : 'outlined'}
              onClick={() => onTabChange(null)}
              sx={{ mr: 0.5 }}
            />
            {tabs.map((tab) => (
              <Stack key={tab.id} direction="row" alignItems="center">
                <Chip
                  icon={tab.icon ? <TabIconGlyph icon={tab.icon} /> : undefined}
                  label={tab.name}
                  size="small"
                  color={activeTabId === tab.id ? 'primary' : 'default'}
                  variant={activeTabId === tab.id ? 'filled' : 'outlined'}
                  onClick={() => onTabChange(tab.id)}
                  onDelete={canEdit ? () => onDeleteTab(tab.id) : undefined}
                />
                <Box sx={{ width: canEdit ? 0 : 8 }} />
              </Stack>
            ))}
            {canEdit && (
              <Chip
                icon={<AddIcon fontSize="small" />}
                label="Add tab"
                size="small"
                variant="outlined"
                onClick={() => setAddTabOpen(true)}
              />
            )}
          </Stack>
        )}

        {gridItems.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mb: canEdit ? 1.5 : 0 }}>
            No cards pinned to this tab yet.
          </Typography>
        ) : (
          <CardGrid
            items={gridItems}
            getId={({ assignment }) => assignment.id}
            getColumnSpan={({ assignment }) => assignment.columnSpan}
            editable={canEdit}
            minSpan={CARD_MIN_SPAN}
            maxSpan={CARD_MAX_SPAN}
            onMove={canEdit ? ({ assignment }, direction) => onMoveCard(assignment.id, direction) : undefined}
            onResize={canEdit ? ({ assignment }, columnSpan) => onResizeCard(assignment.id, columnSpan) : undefined}
            actions={cardGridActions}
            renderCard={({ card }) => <DashboardCardTile card={card} sectionTitleById={NO_SECTION_TITLES} />}
            sx={{ mb: canEdit ? 1.5 : 0 }}
          />
        )}

        {canEdit && availableCards.length > 0 && (
          <FormControl size="small" sx={{ width: 320, maxWidth: '100%' }}>
            <Select
              displayEmpty
              value=""
              onChange={(e) => {
                if (e.target.value) onAddCard(Number(e.target.value), activeTabId);
              }}
              renderValue={() => (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
                  <AddIcon fontSize="small" />
                  <Typography variant="body2">Add card</Typography>
                </Stack>
              )}
            >
              {availableCards.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {(docById.get(c.documentId)?.name ?? 'Untitled document')} - {c.cardTitle}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Collapse>

      <AddTabDialog open={addTabOpen} onClose={() => setAddTabOpen(false)} onSubmit={onAddTab} />
    </GlassPanel>
  );
}
